import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import dealCrawlerService, { CruiseDealCrawler } from '~/services/cruises/DealCrawlerService';

import { parseSearchString, stringifyQuery } from '~/utils/url';

export interface DealCrawlerFilters {
  sourceUrl?: string;
  vendorId?: string;
}

interface CruisesData {
  dealCrawlers: Array<CruiseDealCrawler>;
  page: number;
  total: number;
  isLoading: boolean;
  recordsPerPage: number;
  filters: DealCrawlerFilters;
  setFilters: (filters: DealCrawlerFilters) => void;
  paginate: (page: number) => void;
  getDealCrawlers: () => Promise<void>;
}

const RECORDS_PER_PAGE = 15;

export default function useCruiseDealsCrawler(): CruisesData {
  const { search } = useLocation();
  const { push: setQueryString } = useHistory();
  const [total, setTotal] = useState(0);
  const [dealCrawlers, setDealCrawlers] = useState<Array<CruiseDealCrawler>>([]);
  const [isLoading, setIsLoading] = useState(false);

  const queryParams = useMemo(() => parseSearchString(search), [search]);

  const page = parseInt(queryParams.page as string) || 1;

  const filters: DealCrawlerFilters = useMemo(
    () => ({
      sourceUrl: (queryParams.sourceUrl as string) || undefined,
      vendorId: (queryParams.vendorId as string) || undefined,
    }),
    [queryParams.sourceUrl, queryParams.vendorId],
  );

  const setFilters = useCallback(
    (newFilters: DealCrawlerFilters) => {
      setQueryString({
        search: stringifyQuery({
          page: '1',
          sourceUrl: newFilters.sourceUrl,
          vendorId: newFilters.vendorId,
        }),
      });
    },
    [setQueryString],
  );

  const paginate = useCallback(
    (page: number) => {
      setQueryString({
        search: stringifyQuery({
          page: page.toString(),
          sourceUrl: filters.sourceUrl,
          vendorId: filters.vendorId,
        }),
      });
    },
    [setQueryString, filters],
  );

  const getDealCrawlers = useCallback(async () => {
    try {
      const skip = (page - 1) * RECORDS_PER_PAGE;
      setIsLoading(true);
      const res = await dealCrawlerService.listWithPagination({
        skip,
        take: RECORDS_PER_PAGE,
        ...filters,
      });
      setDealCrawlers(res.result);
      setTotal(res.total);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    getDealCrawlers();
  }, [getDealCrawlers]);

  return {
    dealCrawlers,
    getDealCrawlers,
    page,
    recordsPerPage: RECORDS_PER_PAGE,
    filters,
    setFilters,
    paginate,
    total,
    isLoading,
  };
}
